import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';
import { DrawerOpenEvent } from '@theme/theme-drawer';

/**
 * A custom element that manages cart drawer behavior within a `<theme-drawer>`.
 *
 * Dialog lifecycle (open/close, squeeze, history, animations) is owned by `<theme-drawer>`.
 * The `cart:view` event is auto-dispatched by `CartItemsComponent` via the
 * `view-event-trigger="dialog"` attribute (see `snippets/cart-items-component.liquid`).
 * Cart count announcements are owned by `<header-actions>`.
 * This component handles the remaining cart-specific concerns: auto-open on add-to-cart,
 * sticky summary layout, and the installments CTA close-on-click.
 *
 * @extends {Component}
 */
class CartDrawerComponent extends Component {
  // threadkins custom — raised from the stock 0.5. On real phones the COLLAPSED summary
  // (totals + gift-note toggle + checkout) measures ~0.50-0.58 of the 100dvh dialog
  // (desktop ~0.42), so at 0.5 it un-pinned checkout even with the gift note closed.
  // 0.6 keeps the collapsed summary pinned while the expanded gift panel (~0.83, ~0.67
  // desktop) still flips to static/scrollable — preserving the un-trap behavior.
  /** @type {number} */
  #summaryThreshold = 0.6;

  // threadkins custom — the stock component recomputes the sticky-summary state ONLY on
  // drawer-open and cart-line-change. A pure-CSS reveal that grows the summary (the gift-note
  // toggle, the cart-note / discount accordions) leaves `cart-summary-sticky` stale, so a tall
  // summary stays pinned to the bottom and traps the items until the drawer is reopened. This
  // ResizeObserver re-measures whenever the summary's own height changes OR the dialog resizes
  // (rotation, keyboard). rAF-debounced; no feedback loop (flipping the attribute changes
  // position/overflow, not the summary's border-box height).
  /** @type {Element | null} */
  #observedSummary = null;
  /** @type {number} */
  #rafId = 0;
  #summaryObserver = new ResizeObserver(() => {
    cancelAnimationFrame(this.#rafId);
    this.#rafId = requestAnimationFrame(() => this.#updateStickyState());
  });

  /** @type {import('@theme/theme-drawer').ThemeDrawer | null} */
  get #themeDrawer() {
    return /** @type {import('@theme/theme-drawer').ThemeDrawer | null} */ (this.closest('theme-drawer'));
  }

  /** @type {HTMLDialogElement | null} */
  get #dialog() {
    return this.closest('dialog');
  }

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartLinesUpdate);
    this.#themeDrawer?.addEventListener(DrawerOpenEvent.eventName, this.#handleDrawerOpen);

    // threadkins custom — observe the (stable) dialog so rotation/keyboard-driven size
    // changes re-measure the sticky state. The summary node is (re)observed in #updateStickyState.
    const dialog = this.#dialog;
    if (dialog) this.#summaryObserver.observe(dialog);

    // threadkins custom — a pure-CSS reveal inside the summary (the gift-note toggle, the
    // cart-note / discount <details>) grows it with no cart event, and the cart's hydration
    // morph can replace the summary node out from under the ResizeObserver. Recompute AND
    // re-bind the observer on any change/toggle inside the summary. Document-delegated in the
    // capture phase (so non-bubbling <details> `toggle` events are caught too) — survives the
    // Section-Rendering morph, mirroring how tk-gift-note keeps its own handlers alive.
    document.addEventListener('change', this.#handleSummaryInteraction, true);
    document.addEventListener('toggle', this.#handleSummaryInteraction, true);

    // The restore path sets [open] before this module loads, so the
    // theme-drawer:open event will have already fired. Use the attribute
    // check so this works even before <theme-drawer> upgrades.
    if (this.#themeDrawer?.hasAttribute('open')) {
      this.#handleDrawerOpen();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#handleCartLinesUpdate);
    this.#themeDrawer?.removeEventListener(DrawerOpenEvent.eventName, this.#handleDrawerOpen);
    // threadkins custom
    document.removeEventListener('change', this.#handleSummaryInteraction, true);
    document.removeEventListener('toggle', this.#handleSummaryInteraction, true);
    this.#summaryObserver.disconnect();
    this.#observedSummary = null;
    cancelAnimationFrame(this.#rafId);
  }

  /**
   * threadkins custom — recompute (and re-bind the observer to the live summary node) when a
   * control inside the summary changes/toggles, e.g. the gift-note switch or an accordion.
   * @param {Event} event
   */
  #handleSummaryInteraction = (event) => {
    const target = event.target;
    if (target instanceof Element && target.closest('.cart-drawer__summary')) {
      cancelAnimationFrame(this.#rafId);
      this.#rafId = requestAnimationFrame(() => this.#updateStickyState());
    }
  };

  /**
   * Handles the theme-drawer opening — updates sticky state and wires up the installments CTA.
   */
  #handleDrawerOpen = () => {
    this.#updateStickyState();

    // Close cart drawer when installments CTA is clicked to avoid overlapping dialogs.
    // Re-queried on every open so it survives cart content re-renders that
    // replace the shopify-payment-terms shadow root.
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const cta = this.querySelector('shopify-payment-terms')?.shadowRoot?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', () => this.#themeDrawer?.close(), { once: true });
    });
  };

  /**
   * @param {import('@shopify/events').CartLinesUpdateEvent} event
   */
  #handleCartLinesUpdate = (event) => {
    const shouldAutoOpen = this.hasAttribute('auto-open') && event.action === 'add' && !this.#themeDrawer?.isOpen;

    // When the event originates inside an open MODAL <dialog> (e.g. quick-add),
    // defer the auto-open until that dialog's native `close` fires so its focus
    // restoration runs first — otherwise we'd capture the wrong
    // `#previouslyFocused`. Non-modal dialogs (e.g. the hotspot preview) don't
    // close on add and don't move focus, so `:modal` excludes them.
    const sourceModal = /** @type {HTMLDialogElement | null} */ (
      event.target instanceof Element ? event.target.closest('dialog:modal') : null
    );

    if (shouldAutoOpen && !sourceModal && !this.#isCartEmpty()) {
      this.#themeDrawer?.open();
    }

    event.promise
      ?.then(({ detail }) => {
        const settle = () => requestAnimationFrame(() => this.#updateStickyState());

        if (!shouldAutoOpen || detail?.didError) {
          settle();
          return;
        }

        const openAndSettle = () => {
          if (!this.#themeDrawer?.isOpen) this.#themeDrawer?.open();
          settle();
        };

        if (sourceModal?.open) {
          sourceModal.addEventListener('close', openAndSettle, { once: true });
        } else {
          openAndSettle();
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[cart-drawer] Event promise rejected:', error);
      });
  };

  #isCartEmpty() {
    return Boolean(this.querySelector('.cart-drawer--empty'));
  }

  #updateStickyState() {
    const dialog = this.#dialog;
    if (!dialog) return;

    // Refs do not cross nested `*-component` boundaries (e.g., `cart-items-component`), so we query within the dialog.
    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    // threadkins custom — keep the observer bound to the live summary node (it is replaced on
    // empty<->filled transitions, patched in place otherwise).
    this.#retargetSummary(summary);

    if (!content || !summary) {
      // Ensure the dialog doesn't get stuck in "unsticky" mode when summary disappears (e.g., empty cart).
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    // threadkins custom — never measure a closed/hidden dialog (0/0 => NaN => wrong 'true').
    if (!dialog.open || drawerHeight === 0) return;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
  }

  /**
   * threadkins custom — (re)bind the ResizeObserver to the current summary node.
   * @param {Element | null} summary
   */
  #retargetSummary(summary) {
    if (summary === this.#observedSummary) return;
    if (this.#observedSummary) this.#summaryObserver.unobserve(this.#observedSummary);
    this.#observedSummary = summary;
    if (summary) this.#summaryObserver.observe(summary);
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
